// ─── Connectors on the feedback canvas ───────────────────────────
//
// The canvas's sixth element kind (f-01961): a line joining two elements, so a
// board can say "this causes that" instead of leaving the reader to infer it.
//
// Everything here is pure and DOM-free, for the reason feedback-link.ts and
// feedback-text.ts are: the interesting part of a connector is ARITHMETIC — where
// an anchor is, which anchor a drop meant, whether a click landed on a diagonal
// line — and arithmetic is worth testing rather than eyeballing.
//
// ── The one idea: an END IS AN ANCHOR, NEVER A COORDINATE ────────
//
// A connector that stored two world points would be a line that lies the moment
// anything moves. So each end stores WHAT IT IS ATTACHED TO, and its world
// position is derived on every paint:
//
//   handle — an element plus one of eight named spots on its box (four corners,
//            four edge centres). Move or resize the element and the spot moves
//            with it; no connector bookkeeping at all.
//   point  — a screenshot plus a NORMALISED (u, v) in the image's own natural
//            space. The same anchor a comment pin uses, and deliberately so: it
//            is what lets a line point at the one button it is about, and it
//            survives a crop and a rescale for free.
//   free   — a world point, attached to nothing.
//
// Every end also carries a last-known world (x, y). That is not redundant: it is
// what a `free` end IS, and it is where an attached end falls back to when its
// element is deleted — so removing a screenshot leaves the line where it was
// rather than destroying it.
//
// A comment pin is deliberately NOT connectable. A pin points at a pixel; it is
// not a thing you draw a line to, and its own position is already an anchor.
import { anchorToBox } from './feedback.js';
/** The eight anchors every element offers: four corners, four edge centres.
 *  Same vocabulary as the resize/crop handles, on purpose — one set of names for
 *  "a named spot on a box" across the whole canvas. */
export const CONNECTOR_HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
export function isConnectorHandle(v) {
    return typeof v === 'string' && CONNECTOR_HANDLES.includes(v);
}
/** Where a named anchor sits inside a box, as a 0..1 fraction of each axis. */
const HANDLE_FRACTION = {
    nw: { u: 0, v: 0 },
    n: { u: 0.5, v: 0 },
    ne: { u: 1, v: 0 },
    e: { u: 1, v: 0.5 },
    se: { u: 1, v: 1 },
    s: { u: 0.5, v: 1 },
    sw: { u: 0, v: 1 },
    w: { u: 0, v: 0.5 },
};
/** A named anchor's world position on a box. */
export function connectorHandlePoint(box, handle) {
    const f = HANDLE_FRACTION[handle];
    return { x: box.x + f.u * box.w, y: box.y + f.v * box.h };
}
/** All eight of a box's anchors, for painting the hints and for finding the
 *  nearest one. */
export function connectorAnchorsOf(box) {
    return CONNECTOR_HANDLES.map((handle) => ({ handle, at: connectorHandlePoint(box, handle) }));
}
/**
 * How close (in SCREEN pixels) a drop has to be to a named anchor to take it.
 *
 * Screen rather than world, so the target is the same size under the pointer at
 * every zoom — the same reasoning `pinHitBox` uses for a comment pin. Converted
 * to world units by dividing by the zoom.
 */
export const CONNECTOR_SNAP_PX = 14;
/** How far outside an element's box a drop still counts as "on" it, in screen
 *  pixels. Generous enough that aiming at a corner from outside works. */
export const CONNECTOR_GRAB_PX = 10;
/** The element an end is attached to, or null for a free end. */
export function connectorEndTarget(end) {
    return end.kind === 'free' ? null : end.elementId;
}
/**
 * Candidates in the order `resolveConnectorEnd` requires: TOPMOST FIRST.
 *
 * This exists because the ordering is a rule, not a detail of how a caller
 * happened to accumulate its list. The first version of the canvas built the
 * candidates by kind — every screenshot, then every note, then every link, then
 * every text block — and reversed that, which reads like topmost-first and is
 * not: it made kind the priority, so a text block captured a drop aimed at an
 * image stacked above it, and the image's point anchoring became unreachable
 * wherever the two overlapped.
 */
export function connectorCandidatesTopFirst(candidates) {
    return [...candidates].sort((a, b) => b.z - a.z);
}
/**
 * Which anchor a drop at `at` meant.
 *
 * The rule, in order:
 *   1. Within the snap radius of one of the eight named anchors → that anchor.
 *      Checked FIRST and across every candidate, so aiming at a corner beats
 *      landing inside the box that owns it.
 *   2. Otherwise inside a candidate that supports point anchors (a screenshot) →
 *      a point anchor at exactly that pixel. This is the "attach to any point on
 *      the image, like a comment" half of the request.
 *   3. Otherwise inside any candidate → its NEAREST named anchor, because those
 *      are the only anchors it has and refusing would feel like a dead drop.
 *   4. Otherwise → free.
 *
 * `candidates` must be ordered TOPMOST FIRST, so a drop over two overlapping
 * elements attaches to the one the user can see — pass them through
 * `connectorCandidatesTopFirst`.
 */
export function resolveConnectorEnd(at, candidates, zoom) {
    const k = zoom > 0 ? zoom : 1;
    const snapR = CONNECTOR_SNAP_PX / k;
    const grab = CONNECTOR_GRAB_PX / k;
    // 1. A named anchor, anywhere within reach. Nearest wins, and ties break
    //    towards the topmost candidate because that is the iteration order.
    let best = null;
    for (const c of candidates) {
        for (const { handle, at: p } of connectorAnchorsOf(c.box)) {
            const d2 = (p.x - at.x) ** 2 + (p.y - at.y) ** 2;
            if (d2 > snapR * snapR)
                continue;
            if (!best || d2 < best.d2) {
                best = { end: { kind: 'handle', elementId: c.id, handle, x: p.x, y: p.y }, d2 };
            }
        }
    }
    if (best)
        return best.end;
    for (const c of candidates) {
        if (!withinBox(at, c.box, grab))
            continue;
        // 2. A screenshot takes a point anchor at the exact pixel.
        if (c.crop) {
            const boxU = clamp01((at.x - c.box.x) / (c.box.w || 1));
            const boxV = clamp01((at.y - c.box.y) / (c.box.h || 1));
            // Box space → the image's NATURAL space, so the anchor survives a later
            // crop change exactly as a pin's does.
            const u = c.crop.u + boxU * c.crop.w;
            const v = c.crop.v + boxV * c.crop.h;
            return {
                kind: 'point',
                elementId: c.id,
                u: clamp01(u),
                v: clamp01(v),
                x: c.box.x + boxU * c.box.w,
                y: c.box.y + boxV * c.box.h,
            };
        }
        // 3. Everything else: its nearest named anchor.
        let near = null;
        for (const a of connectorAnchorsOf(c.box)) {
            const d2 = (a.at.x - at.x) ** 2 + (a.at.y - at.y) ** 2;
            if (!near || d2 < near.d2)
                near = { ...a, d2 };
        }
        if (near) {
            return { kind: 'handle', elementId: c.id, handle: near.handle, x: near.at.x, y: near.at.y };
        }
    }
    // 4. Nothing under the pointer.
    return { kind: 'free', x: at.x, y: at.y };
}
/**
 * An end's live world position.
 *
 * `boxes` and `crops` are looked up by element id. A MISSING element is not an
 * error: it means the element has been deleted (or a payload arrived before it
 * did), and the end falls back to its last-known point — which is what keeps a
 * connector on the board when a screenshot it pointed at is removed.
 *
 * A `point` end whose crop currently HIDES it also falls back, for the same
 * reason a pin hidden by a crop stops being painted: a line drawn to a fallback
 * position would be pointing at the wrong pixel, and `null` from the caller's
 * point of view is worse than the last place it was right about.
 */
export function connectorEndWorld(end, candidates) {
    if (end.kind === 'free')
        return { x: end.x, y: end.y };
    const c = candidates.get(end.elementId);
    if (!c)
        return { x: end.x, y: end.y };
    if (end.kind === 'handle')
        return connectorHandlePoint(c.box, end.handle);
    // A point end on an element that has since lost its crop is nonsense; fall back.
    if (!c.crop)
        return { x: end.x, y: end.y };
    const inBox = anchorToBox(end.u, end.v, c.crop);
    if (!inBox)
        return { x: end.x, y: end.y };
    return { x: c.box.x + inBox.u * c.box.w, y: c.box.y + inBox.v * c.box.h };
}
/** True while a `point` end is inside its screenshot's crop window. A hidden end
 *  is still drawn (at its fallback) but is worth reporting, the way a pin's
 *  `hiddenByCrop` is. */
export function connectorEndHiddenByCrop(end, candidates) {
    if (end.kind !== 'point')
        return false;
    const c = candidates.get(end.elementId);
    if (!c?.crop)
        return false;
    return anchorToBox(end.u, end.v, c.crop) === null;
}
/**
 * The line colours.
 *
 * Deliberately the SAME `--fbt-edge-*` tokens a text block's frame uses, minus
 * its `none`: a line is a line, and inventing a third absolute colour family for
 * one would put two nearly-identical reds in the app for no reason. Absolute
 * rather than theme-following for the reason those are — a connector is drawn ON
 * a board over screenshots, not on an app surface, so it must not move when the
 * palette does.
 */
export const FEEDBACK_CONNECTOR_COLORS = [
    { key: 'black', label: 'Black', token: '--fbt-edge-black' },
    { key: 'grey', label: 'Grey', token: '--fbt-edge-grey' },
    { key: 'slate', label: 'Slate', token: '--fbt-edge-slate' },
    { key: 'red', label: 'Red', token: '--fbt-edge-red' },
    { key: 'amber', label: 'Amber', token: '--fbt-edge-amber' },
    { key: 'green', label: 'Green', token: '--fbt-edge-green' },
    { key: 'teal', label: 'Teal', token: '--fbt-edge-teal' },
    { key: 'blue', label: 'Blue', token: '--fbt-edge-blue' },
    { key: 'violet', label: 'Violet', token: '--fbt-edge-violet' },
];
export const FEEDBACK_CONNECTOR_DEFAULT_COLOR = 'black';
export function feedbackConnectorColorDef(key) {
    return FEEDBACK_CONNECTOR_COLORS.find((c) => c.key === key) ?? FEEDBACK_CONNECTOR_COLORS[0];
}
/** Line thickness and arrowhead size, in WORLD units — so both scale with the
 *  board like every other element's content, rather than staying a constant
 *  screen size the way the selection ring and the alignment guides do. */
export const CONNECTOR_STROKE_W = 2;
export const CONNECTOR_ARROW_LEN = 13;
export const CONNECTOR_ARROW_W = 10;
/** Shortest a connector may be drawn. Below this the direction is meaningless,
 *  so the arrowheads would spin as the pointer jitters. */
export const CONNECTOR_MIN_LEN = 1;
/**
 * The arrowhead as a triangle, in world units.
 *
 * Computed rather than left to an SVG `<marker>`: a marker needs a `<defs>` entry
 * per colour (or `context-stroke`, which not every engine honours the same way),
 * and its `markerUnits` scaling then has to be reasoned about on top of the
 * board's own transform. Three points of trigonometry has none of that and draws
 * identically everywhere.
 *
 * `from` only supplies the DIRECTION, so it can be either the other end or any
 * point along the shaft.
 */
export function connectorArrowPoints(tip, from, len = CONNECTOR_ARROW_LEN, width = CONNECTOR_ARROW_W) {
    const dx = tip.x - from.x;
    const dy = tip.y - from.y;
    const d = Math.hypot(dx, dy);
    // A zero-length shaft has no direction; point the head along +x rather than
    // producing NaN, which in SVG is an invisible element and an unexplainable bug.
    const ux = d > 0 ? dx / d : 1;
    const uy = d > 0 ? dy / d : 0;
    // The base's centre, then out along the perpendicular.
    const bx = tip.x - ux * len;
    const by = tip.y - uy * len;
    const px = -uy * (width / 2);
    const py = ux * (width / 2);
    return [tip, { x: bx + px, y: by + py }, { x: bx - px, y: by - py }];
}
/**
 * Distance from a point to the segment a→b.
 *
 * The connector's hit test. A bounding box will not do: a diagonal line's box is
 * mostly empty, so a box test would hand a click to a connector the user was
 * nowhere near — and on a board full of them, would make the elements underneath
 * unclickable.
 */
export function pointToSegmentDistance(p, a, b) {
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len2 = vx * vx + vy * vy;
    if (len2 === 0)
        return Math.hypot(p.x - a.x, p.y - a.y);
    // How far along the segment the nearest point is, clamped to its ends.
    let t = ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy));
}
/** How close (SCREEN px) a press has to be to the line to select it. Comfortably
 *  wider than the stroke: a 2px line is not a 2px target. */
export const CONNECTOR_HIT_PX = 8;
/** The connector's bounding box, padded for the stroke and the arrowheads. Used
 *  for the marquee and to size the element's SVG. */
export function connectorBounds(a, b) {
    const pad = Math.max(CONNECTOR_ARROW_LEN, CONNECTOR_STROKE_W * 2);
    const x = Math.min(a.x, b.x) - pad;
    const y = Math.min(a.y, b.y) - pad;
    return {
        x,
        y,
        w: Math.abs(a.x - b.x) + pad * 2,
        h: Math.abs(a.y - b.y) + pad * 2,
    };
}
function withinBox(p, box, slack) {
    return p.x >= box.x - slack && p.x <= box.x + box.w + slack
        && p.y >= box.y - slack && p.y <= box.y + box.h + slack;
}
const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);
/**
 * Re-anchor an end onto a DIFFERENT board's elements, or free it.
 *
 * A pasted connector keeps an attachment only when the element it names is
 * actually present. On its own board that is every attachment; on another board
 * it is none, and both ends land free where the copy was placed — which is a
 * line you can then re-attach, rather than a paste that fails or one that draws
 * to nowhere. (The same reasoning that lets a note paste anywhere and keeps a
 * screenshot board-bound.)
 */
export function rebaseConnectorEnd(end, present, offset) {
    const moved = { x: end.x + offset.x, y: end.y + offset.y };
    if (end.kind === 'free')
        return { kind: 'free', ...moved };
    if (!present.has(end.elementId))
        return { kind: 'free', ...moved };
    // Kept attached: the anchor decides the position, so the offset is irrelevant
    // and the stored x/y is only a fallback.
    return end.kind === 'handle'
        ? { ...end }
        : { ...end };
}
/**
 * One connector end, validated.
 *
 * Everything reaching here is client-supplied JSON — a PATCH body, a clipboard
 * payload, an MCP argument — so it is treated as hostile rather than as something
 * we wrote. Returns null for anything unrecognised instead of a half-built end: a
 * `handle` kind with no handle would be a line silently drawn to the top-left of
 * something, and a NaN coordinate would put an end somewhere unreachable and
 * unselectable (the lesson `decodeFeedbackClip` already learned).
 *
 * `x`/`y` are required whatever the kind, because they are the fallback an
 * attached end needs when its element is deleted.
 */
export function parseConnectorEnd(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const e = raw;
    const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const x = num(e.x);
    const y = num(e.y);
    if (x === null || y === null)
        return null;
    if (e.kind === 'free')
        return { kind: 'free', x, y };
    const id = typeof e.elementId === 'string' && e.elementId ? e.elementId : null;
    if (!id)
        return null;
    if (e.kind === 'handle') {
        return isConnectorHandle(e.handle)
            ? { kind: 'handle', elementId: id, handle: e.handle, x, y }
            : null;
    }
    if (e.kind === 'point') {
        const u = num(e.u);
        const v = num(e.v);
        if (u === null || v === null)
            return null;
        // Clamped rather than refused: an anchor a hair outside the image is a
        // rounding artefact, not an intent — the same call feedback_comments makes.
        return { kind: 'point', elementId: id, u: clamp01(u), v: clamp01(v), x, y };
    }
    return null;
}
//# sourceMappingURL=feedback-connector.js.map